import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';
import { DeleteConfirmDialog } from '@src/design-system/molecules/delete-confirm-dialog-button';
import reminderApi from '@src/features/reminders/services/reminder-api';
import { CreateOrEditReminder } from '@src/shared/features/reminders/dialogs/create-or-edit-reminder';
import type { TReminder } from '@src/shared/types/reminder';
import type { Row } from '@tanstack/react-table';
import { CloseCircle, CloseSquare, Eye } from 'iconsax-react';

export function UpcomingReminderCancelButton({ row }: { row: Row<TReminder> }) {
  const reminder = row.original;
  const { mutate: deleteReminder, isPending } = reminderApi.useDelete();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='iconSm'
          className='text-red-600'
          aria-label='Cancel reminder'
        >
          <CloseSquare
            size={18}
            variant='TwoTone'
          />
        </Button>
      </DialogTrigger>
      <DialogContent
        align='center'
        className='max-w-[560px]'
      >
        <DialogHeader className='p-4'>Cancel Reminder</DialogHeader>
        <DeleteConfirmDialog
          icon={
            <CloseCircle
              size={28}
              className='text-red-600'
              variant='TwoTone'
            />
          }
          title={
            <div className='flex flex-col gap-2 items-center justify-center text-center'>
              <p className='text-gray-700 text-lg font-medium'>
                Are you sure you want to cancel this reminder?
              </p>
              <p className='text-gray-700 w-[90%]'>
                This action cannot be undone. All associated data will be lost.
              </p>
            </div>
          }
          isLoading={isPending}
          onConfirm={(closeModal) => {
            deleteReminder(
              { id: reminder.id },
              {
                onSuccess: closeModal,
              }
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export function UpcomingReminderViewButton({ row }: { row: Row<TReminder> }) {
  const reminder = row.original;

  return (
    <Dialog>
      <DialogTrigger
        disabled
        asChild
      >
        <Button
          type='button'
          variant='ghost'
          size='iconSm'
          aria-label='View reminder'
        >
          <Eye
            size={18}
            variant='TwoTone'
          />
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-[min(1140px,calc(100vw-2rem))] max-h-[90vh] overflow-y-auto'>
        <CreateOrEditReminder reminder={reminder} />
      </DialogContent>
    </Dialog>
  );
}
